import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text, Spinner, SpinnerSize, Dropdown, IDropdownOption,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  CaretLeft, Clock, ArrowsClockwise, Phone, Warning, ChartBar,
} from '@phosphor-icons/react';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import { accessService } from '../services/accessService';

// ── Palette & constants ──
const p = boroondaraPalette;
const GREEN = '#2e873d';

const GREEN_PALETTE = ['#2e873d', '#55a655', '#7BC47B', '#A8DCA8', '#D4F0D4', '#1a5c26', '#3d9e4d', '#6db86d'];
const SUNSET_PALETTE = ['#E76F51', '#F4A261', '#E9C46A', '#2A9D8F', '#264653', '#D4623C', '#F08C4A', '#C94C30'];

const TAB_CONFIG = [
  { id: 'contacts', label: 'Contacts', icon: Phone, color: GREEN, palette: GREEN_PALETTE },
  { id: 'incidents', label: 'Incidents', icon: Warning, color: '#E76F51', palette: SUNSET_PALETTE },
];

const REFRESH_OPTIONS: IDropdownOption[] = [
  { key: 30, text: '30 seconds' },
  { key: 60, text: '1 minute' },
  { key: 300, text: '5 minutes' },
  { key: 600, text: '10 minutes' },
];

const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });
const pulseAnim = keyframes({ '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 } });
const tooltipStyle = { backgroundColor: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 4, color: '#232323', fontSize: 12 };

// ── Helpers ──
function fmtNum(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + 'K';
  return String(Math.round(value));
}

// ── Styles ──
const s = mergeStyleSets({
  page: { padding: 32, minHeight: 'calc(100vh - 52px)', background: '#F5F5F5', '@media (max-width: 768px)': { padding: 16 } } as any,
  container: { maxWidth: 1240, margin: '0 auto' },
  headerRow: { marginBottom: 28 },
  title: { color: p.text, fontWeight: 800 as any, letterSpacing: '-0.02em' },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 720, lineHeight: '1.6' },
  timestamp: { color: p.text3, fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const },
  liveDot: { width: 8, height: 8, borderRadius: '50%', background: GREEN, display: 'inline-block', animationName: pulseAnim, animationDuration: '1.5s', animationIterationCount: 'infinite' },
  tabRow: { display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap' as const },
  tab: { padding: '8px 18px', borderRadius: 4, fontSize: 13, fontWeight: 600 as any, border: '1px solid #EDEDED', background: '#FFFFFF', color: '#484848', cursor: 'pointer', transition: 'all 0.15s' },
  tabActive: { background: '#d9f4d7', borderColor: 'rgba(46,135,61,0.4)', color: GREEN },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28, '@media (max-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 } } as any,
  kpiCard: { padding: 22, borderRadius: 4, background: '#FFFFFF', border: '1px solid #E1E1E1', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' },
  kpiIconWrap: { width: 36, height: 36, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  kpiLabel: { color: p.text3, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' as any, fontWeight: 600 as any, marginBottom: 8 },
  kpiValue: { color: p.text, fontWeight: 700 as any, fontSize: 28, lineHeight: '36px', letterSpacing: '-0.02em', marginBottom: 4, '@media (max-width: 768px)': { fontSize: 22, lineHeight: '28px' } } as any,
  kpiHint: { color: p.text3, fontSize: 12 },
  chartsRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20, '@media (max-width: 768px)': { gridTemplateColumns: '1fr' } } as any,
  chartCard: { padding: 24, borderRadius: 4, background: '#FFFFFF', border: '1px solid #EDEDED', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', '@media (max-width: 768px)': { padding: 14 } } as any,
  chartTitle: { color: p.text, fontWeight: 600 as any, fontSize: 15, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },
  tableCard: { padding: 24, borderRadius: 4, background: '#FFFFFF', border: '1px solid #EDEDED', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20 },
  tableTitle: { color: p.text, fontWeight: 600 as any, fontSize: 15, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 },
  th: { background: '#F5F5F5', padding: '10px 12px', textAlign: 'left' as const, fontWeight: 600, color: '#484848', borderBottom: '2px solid #E1E1E1', whiteSpace: 'nowrap' as const, position: 'sticky' as const, top: 0, zIndex: 1 },
  td: { padding: '8px 12px', borderBottom: '1px solid #F0F0F0', color: '#232323' },
  trStriped: { background: '#FAFAFA' },
  centerWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 },
  noData: { color: p.text3, fontSize: 13, textAlign: 'center' as any, padding: 40 },
  refreshBtn: {
    marginLeft: 4, color: p.primary, fontSize: 12, fontWeight: 600 as any, height: 28, padding: '0 10px', borderRadius: 4,
    border: `1px solid ${p.borderSoft}`, background: '#FFFFFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'all 0.15s ease',
    selectors: { ':hover': { background: '#F0F6FF', borderColor: p.primary }, ':disabled': { opacity: 0.6, cursor: 'default' } },
  } as any,
  spinning: { animationName: spinAnim, animationDuration: '1s', animationIterationCount: 'infinite', animationTimingFunction: 'linear', display: 'inline-block' },
  error: { marginBottom: 20 },
  countdown: { color: p.text3, fontSize: 11, marginLeft: 4 },
});

// ── Types ──
interface KpiEntry { label: string; raw: number | string; suffix?: string }
interface TabData {
  kpis?: Record<string, KpiEntry>;
  charts?: Record<string, any[]>;
  table?: any[];
}

// ══════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════
export default function RealtimeDashboard() {
  const nav = useNavigate();

  // ── Access control ──
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{
          role?: string;
          canAccessRealtime?: boolean;
        }>('/me');
        if (!alive) return;
        const d = res.data;
        if (d.role === 'admin') {
          setHasAccess(true);
          setIsAdmin(true);
        } else {
          setHasAccess(d.canAccessRealtime === true);
        }
      } catch {
        // no access
      }
      if (alive) setAccessChecked(true);
    })();
    return () => { alive = false; };
  }, []);

  // ── State ──
  const [activeTab, setActiveTab] = useState('contacts');
  const [data, setData] = useState<TabData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState('');
  const [refreshInterval, setRefreshInterval] = useState<number>(60);
  const [countdown, setCountdown] = useState(60);
  const intervalRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);

  // ── Fetch config ──
  useEffect(() => {
    (async () => {
      try {
        const res = await accessService.get<{ refreshIntervalSeconds: number }>('/realtime/config');
        if (res.data?.refreshIntervalSeconds) {
          setRefreshInterval(res.data.refreshIntervalSeconds);
          setCountdown(res.data.refreshIntervalSeconds);
        }
      } catch { /* use default */ }
    })();
  }, []);

  // ── Fetch data ──
  const fetchData = useCallback(async (tab: string, isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await accessService.get(`/realtime/${tab}`);
      setData(res.data);
      setLoadedAt(new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setCountdown(refreshInterval);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [refreshInterval]);

  // ── Initial fetch + refetch on tab change ──
  useEffect(() => {
    if (accessChecked && hasAccess) {
      fetchData(activeTab);
    }
  }, [activeTab, fetchData, accessChecked, hasAccess]);

  // ── Auto-refresh interval ──
  useEffect(() => {
    if (!accessChecked || !hasAccess || !refreshInterval) return;

    // Clear existing
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    intervalRef.current = window.setInterval(() => {
      fetchData(activeTab, true);
    }, refreshInterval * 1000);

    countdownRef.current = window.setInterval(() => {
      setCountdown(prev => (prev > 1 ? prev - 1 : refreshInterval));
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [refreshInterval, activeTab, fetchData, accessChecked, hasAccess]);

  // ── Admin: update refresh config ──
  const handleRefreshChange = async (_: any, opt?: IDropdownOption) => {
    if (!opt) return;
    const seconds = opt.key as number;
    setRefreshInterval(seconds);
    setCountdown(seconds);
    try {
      await accessService.post('/realtime/config', { refreshIntervalSeconds: seconds });
    } catch { /* ignore */ }
  };

  const activeTabConfig = TAB_CONFIG.find(t => t.id === activeTab)!;
  const tabColor = activeTabConfig.color;
  const tabPalette = activeTabConfig.palette;

  const kpis = data?.kpis || {};
  const charts = data?.charts || {};
  const table = data?.table || [];

  // ── Render KPI value ──
  const renderKpiValue = (kpi: KpiEntry) => {
    if (kpi.suffix === 'text') return String(kpi.raw);
    if (kpi.suffix === 'min') return fmtNum(Number(kpi.raw)) + ' min';
    if (kpi.suffix === 'hrs') return fmtNum(Number(kpi.raw)) + ' hrs';
    if (kpi.suffix === 'days') return fmtNum(Number(kpi.raw)) + ' days';
    return fmtNum(Number(kpi.raw));
  };

  // ── Format month labels ──
  const fmtMonth = (v: string) => {
    if (!v) return '';
    const d = new Date(v);
    return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
  };

  // ── Countdown format ──
  const countdownText = countdown >= 60
    ? `${Math.floor(countdown / 60)}m ${countdown % 60}s`
    : `${countdown}s`;

  // ══════════════════════════════════════════════════════════
  // Access control gate
  // ══════════════════════════════════════════════════════════
  if (!accessChecked) {
    return (
      <div className={s.page}>
        <div className={s.centerWrap}>
          <Spinner size={SpinnerSize.large} label="Checking access..." />
        </div>
      </div>
    );
  }
  if (!hasAccess) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => nav('/dashboards')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: p.primary, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CaretLeft size={10} /> Dashboards
            </button>
          </div>
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <Text variant="xLarge" style={{ fontWeight: 700, color: p.text, display: 'block', marginBottom: 12 }}>Access Required</Text>
            <Text variant="medium" style={{ color: p.text2 }}>
              This is a governed dashboard. Contact your administrator to request access to Real-Time Operations data.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // Loading
  // ══════════════════════════════════════════════════════════
  if (loading && !data) {
    return (
      <div className={s.page}>
        <div className={s.centerWrap}>
          <Spinner size={SpinnerSize.large} label="Loading Real-Time Dashboard..." />
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════
  return (
    <div className={s.page}>
      <div className={s.container}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => nav('/dashboards')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: p.primary, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CaretLeft size={10} /> Dashboards
          </button>
        </div>

        {/* Header */}
        <div className={s.headerRow}>
          <Text variant="xxLarge" className={s.title}>Real-Time Operations</Text>
          <Text variant="medium" className={s.subtitle}>
            Live contacts and incidents from Fabric Eventhouse with auto-refresh.
          </Text>
          <div className={s.timestamp}>
            <span className={s.liveDot} />
            <span style={{ color: GREEN, fontWeight: 600 }}>LIVE</span>
            <Clock size={12} color={p.text3} />
            Last updated: {loadedAt}
            <span className={s.countdown}>Next refresh in {countdownText}</span>
            <button className={s.refreshBtn} onClick={() => fetchData(activeTab, true)} disabled={refreshing}>
              <ArrowsClockwise size={12} className={refreshing ? s.spinning : undefined} />
              {refreshing ? 'Refreshing...' : 'Refresh Now'}
            </button>
            {isAdmin && (
              <Dropdown
                selectedKey={refreshInterval}
                options={REFRESH_OPTIONS}
                onChange={handleRefreshChange}
                styles={{ dropdown: { minWidth: 130, border: '1px solid #E1E1E1', borderRadius: 4 }, title: { fontSize: 12, fontWeight: 500, color: '#232323', borderRadius: 4 } }}
              />
            )}
          </div>
        </div>

        {/* Tab row */}
        <div className={s.tabRow}>
          {TAB_CONFIG.map(tab => (
            <button key={tab.id} className={`${s.tab} ${activeTab === tab.id ? s.tabActive : ''}`} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={s.error}>
            {error}
            <button className={s.refreshBtn} onClick={() => fetchData(activeTab)} style={{ marginLeft: 12 }}>
              <ArrowsClockwise size={12} /> Retry
            </button>
          </MessageBar>
        )}

        {/* ══ CONTACTS TAB ══ */}
        {activeTab === 'contacts' && data && (
          <>
            {/* KPIs */}
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiIconWrap} style={{ background: tabColor + '18', border: `1px solid ${tabColor}30` }}>
                    <Phone size={16} color={tabColor} />
                  </div>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div className={s.chartsRow}>
              {charts.byMonth && charts.byMonth.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartTitle}><ChartBar size={16} color={tabColor} />Contacts Created by Month</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.byMonth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Month" tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={fmtMonth} />
                      <YAxis tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtMonth} />
                      <Bar dataKey="Count" radius={[4, 4, 0, 0]} barSize={32}>
                        {charts.byMonth.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.byCountry && charts.byCountry.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartTitle}><ChartBar size={16} color={tabColor} />Contacts by Country</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.byCountry} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Country" tick={{ fontSize: 11, fill: '#484848' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="Count" radius={[4, 4, 0, 0]} barSize={40}>
                        {charts.byCountry.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Detail table */}
            {table.length > 0 && (
              <div className={s.tableCard}>
                <div className={s.tableTitle}><ChartBar size={16} color={tabColor} />Contact Register (Latest State)</div>
                <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        {Object.keys(table[0]).map(col => <th key={col} className={s.th}>{col}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row: any, ri: number) => (
                        <tr key={ri} className={ri % 2 === 1 ? s.trStriped : undefined}>
                          {Object.values(row).map((val: any, ci: number) => (
                            <td key={ci} className={s.td}>{val != null ? String(val) : '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!table.length && !error && (
              <div className={s.noData}>No contacts data available yet. Data will appear once the Eventhouse is configured.</div>
            )}
          </>
        )}

        {/* ══ INCIDENTS TAB ══ */}
        {activeTab === 'incidents' && data && (
          <>
            {/* KPIs */}
            <div className={s.kpiRow}>
              {Object.entries(kpis).map(([key, kpi]) => (
                <div key={key} className={s.kpiCard}>
                  <div className={s.kpiIconWrap} style={{ background: tabColor + '18', border: `1px solid ${tabColor}30` }}>
                    <Warning size={16} color={tabColor} />
                  </div>
                  <div className={s.kpiLabel}>{kpi.label}</div>
                  <div className={s.kpiValue}>{renderKpiValue(kpi)}</div>
                </div>
              ))}
            </div>

            {/* Charts row 1: Status pie + Category bar */}
            <div className={s.chartsRow}>
              {charts.byStatus && charts.byStatus.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartTitle}><ChartBar size={16} color={tabColor} />Incidents by Status</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={charts.byStatus} dataKey="Count" nameKey="Status" cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2}>
                        {charts.byStatus.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.byCategory && charts.byCategory.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartTitle}><ChartBar size={16} color={tabColor} />All Incidents by Category</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.byCategory} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="Category" width={140} tick={{ fontSize: 10, fill: '#484848' }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="Count" radius={[0, 4, 4, 0]} barSize={18}>
                        {charts.byCategory.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Charts row 2: Queue workload + Monthly trend */}
            <div className={s.chartsRow}>
              {charts.byQueue && charts.byQueue.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartTitle}><ChartBar size={16} color={tabColor} />Open Backlog by Queue</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.byQueue} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#747474' }} />
                      <YAxis type="category" dataKey="Queue" width={140} tick={{ fontSize: 10, fill: '#484848' }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="OpenCount" radius={[0, 4, 4, 0]} barSize={18}>
                        {charts.byQueue.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.trend && charts.trend.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartTitle}><ChartBar size={16} color={tabColor} />Monthly Trend: Created vs Closed</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.trend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Month" tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={fmtMonth} />
                      <YAxis tick={{ fontSize: 11, fill: '#747474' }} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtMonth} />
                      <Legend />
                      <Bar dataKey="Created" fill={tabPalette[0]} radius={[4, 4, 0, 0]} barSize={20} />
                      <Bar dataKey="Closed" fill={tabPalette[3]} radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Detail table */}
            {table.length > 0 && (
              <div className={s.tableCard}>
                <div className={s.tableTitle}><ChartBar size={16} color={tabColor} />Open Incidents (with Age in Days)</div>
                <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        {Object.keys(table[0]).map(col => <th key={col} className={s.th}>{col}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {table.map((row: any, ri: number) => (
                        <tr key={ri} className={ri % 2 === 1 ? s.trStriped : undefined}>
                          {Object.values(row).map((val: any, ci: number) => (
                            <td key={ci} className={s.td}>{val != null ? String(val) : '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!table.length && !error && (
              <div className={s.noData}>No incidents data available yet. Data will appear once the Eventhouse is configured.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
